/**
 * Converts rotational frequency (rpm) to angular velocity (rad/s).
 * Used as an intermediate value in every unbalance formula.
 *
 * @param {number} frequency - Rotation speed in rpm
 * @returns {number} Angular velocity ω in rad/s
 */
export const RadialSpeed = (frequency) => (Math.PI * +frequency) / 30.0;

/**
 * Derives specific unbalance (e) from total unbalance and rotor mass.
 * Specific unbalance is the eccentricity of the center of mass in μm.
 *
 * @param {number} residualDisbalance - Total allowed unbalance in g·mm
 * @param {number} mass - Rotor mass in kg
 * @returns {number} Specific unbalance in μm (= g·mm / kg)
 */
export const SpecificResidualDisbalance = (residualDisbalance, mass) =>
  +residualDisbalance / +mass;

/**
 * Derives specific unbalance (e) from total unbalance and angular velocity.
 * Alternative form used when the tolerance class provides e·ω (mm/s) directly.
 *
 * @param {number} residualDisbalance - Total allowed unbalance in g·mm
 * @param {number} radialSpeed - Angular velocity ω in rad/s
 * @returns {number} Specific unbalance in μm
 */
export const SpecificResidualDisbalanceByRadialSpeed = (residualDisbalance, radialSpeed) => 
  (1000 * +residualDisbalance) / +radialSpeed;

/**
 * Calculates the total permissible residual unbalance for a rotor.
 * Derived from ISO 21940: U = e × m, re-expressed using the tolerance
 * grade value (e·ω in mm/s) as the input.
 *
 * @param {number} residualSpecificDisbalance - Tolerance grade value (e·ω) in mm/s
 * @param {number} mass - Rotor mass in kg
 * @param {number} radialSpeed - Angular velocity ω in rad/s
 * @returns {number} Total allowed unbalance in g·mm
 */
export const ResidualDisbalance = (
  residualSpecificDisbalance,
  mass,
  radialSpeed
) => (1000 * +residualSpecificDisbalance * +mass) / +radialSpeed;

/**
 * Distributes total unbalance to one correction plane using the lever rule.
 * For dual-plane balancing, each plane's share is inversely proportional to
 * its distance from the center of mass (moment balance).
 *
 * @param {number} residualDisbalance - Total allowed unbalance in g·mm
 * @param {number} length - Distance between the two correction planes in mm
 * @param {number} lengthToMassCenter - Distance from the OTHER plane to the center of mass in mm
 * @returns {number} Allowed unbalance in this plane in g·mm
 */
export const ResidualDisbalanceByPlane = (
  residualDisbalance,
  length,
  lengthToMassCenter
) => (+residualDisbalance * +lengthToMassCenter) / +length;

/**
 * Calculates the permissible residual unbalanced mass at a given correction radius.
 *
 * @param {number} disbalanceInPlain - Allowed unbalance in g·mm
 * @param {number} radiusOfPart - Correction radius in mm
 * @returns {number} Maximum unbalanced mass in grams
 */
export const MassToCompensate = (disbalanceInPlain, radiusOfPart) =>
  +disbalanceInPlain / +radiusOfPart;

/**
 * Calculates the centrifugal force produced by an unbalanced mass.
 * Formula: F = m·ω²·r
 *
 * @param {number} mass - Unbalanced mass in kg
 * @param {number} radialSpeed - Angular velocity ω in rad/s
 * @param {number} radius - Eccentricity (distance from rotation axis) in m
 * @returns {number} Centrifugal force in Newtons
 */
export const CentrifugalForce = (mass, radialSpeed, radius) =>
  (+mass * +radialSpeed * +radialSpeed * +radius);

/**
 * Clamps per-plane unbalance to the valid range for a between-supports rotor.
 * ISO 21940 requires each plane to carry at least 30% and at most 70% of the
 * total unbalance when the rotor is mounted between supports.
 *
 * @param {number} residualDisbalance - Total allowed unbalance
 * @param {number} residualDisbalanceInPlane - Geometrically calculated per-plane value
 * @returns {number} Clamped per-plane unbalance
 */
const ValidResidualDisbalanceInMultiSupport = (
  residualDisbalance,
  residualDisbalanceInPlane
) => {
  if (0.3 * +residualDisbalance >= +residualDisbalanceInPlane) {
    return 0.3 * +residualDisbalance;
  }

  if (+residualDisbalanceInPlane >= 0.7 * +residualDisbalance) {
    return 0.7 * +residualDisbalance;
  }

  return +residualDisbalanceInPlane;
};

/**
 * Clamps per-plane unbalance to the valid range for a cantilever rotor.
 * ISO 21940 allows up to 130% per plane for cantilever (overhung) mounting
 * because both correction planes are on the same side of the supports —
 * correcting one plane can increase the moment contribution from the other.
 *
 * @param {number} residualDisbalance - Total allowed unbalance
 * @param {number} residualDisbalanceInPlane - Geometrically calculated per-plane value
 * @returns {number} Clamped per-plane unbalance
 */
const ValidResidualDisbalanceInConsole = (
  residualDisbalance,
  residualDisbalanceInPlane
) => {
  if (0.3 * +residualDisbalance >= +residualDisbalanceInPlane) {
    return 0.3 * +residualDisbalance;
  }

  if (+residualDisbalanceInPlane >= 1.3 * +residualDisbalance) {
    return 1.3 * +residualDisbalance;
  }

  return residualDisbalanceInPlane;
};

/**
 * Returns the clamped per-plane unbalance for the given mount type.
 *
 * @param {string} mountType - One of the MountTypes values
 * @param {number} residualDisbalance - Total allowed unbalance
 * @param {number} residualDisbalancePerPlane - Geometrically calculated per-plane value
 * @returns {number|false} Clamped per-plane unbalance, or false for an unknown mount type
 */
export const validResidualDisbalance = (
  mountType,
  residualDisbalance,
  residualDisbalancePerPlane
) => {
  switch (mountType) {
    case MountTypes.multiSupport:
      return ValidResidualDisbalanceInMultiSupport(
        residualDisbalance,
        residualDisbalancePerPlane
      );
    case MountTypes.console:
      return ValidResidualDisbalanceInConsole(
        residualDisbalance,
        residualDisbalancePerPlane
      );
    default:
      return false;
  }
};

/**
 * Guards against NaN and Infinity before displaying a calculated value.
 * Inputs are often empty or zero while the user is still typing, which
 * produces invalid intermediate results that must not be shown in the UI.
 *
 * @param {number} value
 * @returns {number|null} The value, or null if it is NaN or non-finite
 */
export const ValueOrNull = (value) => {
  if (isNaN(value)) {
    return null;
  }
  if (isFinite(value)) {
    return value;
  }
  return null;
};

/**
 * Returns the mass unit options for a unit-selector dropdown.
 * Each entry's `value` is the multiplier that converts the user's input
 * in that unit to kilograms (the standard internal unit for calculations).
 *
 * @param {Function} t - i18next translation function
 * @returns {{ value: number, label: string }[]}
 */
export function getMassUnits(t) {
  return [
    {
      value: 10e5,
      label: t("units.mass.mg"),
    },
    {
      value: 10e2,
      label: t("units.mass.g"),
    },
    {
      value: 1,
      label: t("units.mass.kg"),
    },
    {
      value: 10e-4,
      label: t("units.mass.t"),
    },
  ];
}

/**
 * Returns the unbalance unit options for a unit-selector dropdown.
 * Each entry's `value` is the multiplier that converts the user's input
 * in that unit to g·mm (the standard internal unit for calculations).
 *
 * @param {Function} t - i18next translation function
 * @returns {{ value: number, label: string }[]}
 */
export function getDisbalanceUnits(t) {
  return [
    // {
    //   value: 10E2,
    //   label: "г·мкм",
    // },
    {
      value: 1,
      label: t("units.disbalance.gmm"),
    },
    {
      value: 10e-2,
      label: t("units.disbalance.gcm"),
    },
    {
      value: 10e-4,
      label: t("units.disbalance.gm"),
    },
    // {
    //   value: 10E-4,
    //   label: "кг*см",
    // },
    {
      value: 10e-7,
      label: t("units.disbalance.kgm"),
    },
  ];
}

/**
 * Returns the force unit options for a unit-selector dropdown.
 * Each entry's `value` is the multiplier that converts the user's input
 * in that unit to Newtons.
 *
 * @param {Function} t - i18next translation function
 * @returns {{ value: number, label: string }[]}
 */
export function getForceUnits(t) {
  return [
    {
      value: 10e2,
      label: t("units.force.mN"),
    },
    {
      value: 1,
      label: t("units.force.N"),
    },
    {
      value: 10e-4,
      label: t("units.force.kN"),
    },
  ];
}

/**
 * Returns the residual disbalance unit options. Currently only mm/s is supported.
 *
 * NOTE: the key "units.residualDisbalance.mmps" does not exist in translation.json —
 * the correct key is "units.residualDisbalance.mmDs". Until fixed, the label falls
 * back to the raw key string.
 *
 * @param {Function} t - i18next translation function
 * @returns {{ value: number, label: string }[]}
 */
export function getResidualDisbalanceUnits(t) {
  return [
    {
      value: 1,
      label: t("units.residualDisbalance.mmps"),
    },
  ];
}

/** Supported rotor mount types for dual-plane balancing. */
export const MountTypes = {
  console: "console",
  multiSupport: "multi-support",
};

/** Supported balancing plane configurations. */
export const CalculationMethods = {
  onePlane: "one-plane",
  dualPlane: "dual-plane",
};
